import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, errorMessage, userTimezone } from "@/lib/api";
import { aiLeftOf, usePlan } from "@/lib/plan";
import { SECTIONS } from "@/components/SessionCard";

const lines = (v) => v.split("\n").map((x) => x.trim().slice(0, 200)).filter(Boolean).slice(0, 12);

function EditorSheet({ open, onOpenChange, title, desc, testid, children }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="bg-[#0A0A0A] border-[#27272A] max-w-md mx-auto rounded-t-3xl max-h-[90vh] overflow-y-auto px-5 pb-10" data-testid={testid}>
        <SheetHeader className="text-left mb-5">
          <SheetTitle className="font-display text-2xl uppercase tracking-tight">{title}</SheetTitle>
          <SheetDescription className="text-[#71717A]">{desc}</SheetDescription>
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  );
}

export function EditSessionSheet({ session, open, onOpenChange }) {
  const qc = useQueryClient();
  const init = () => ({ title: session.title, summary: session.summary || "", ...Object.fromEntries(SECTIONS.map((s) => [s.key, (session[s.key] || []).join("\n")])) });
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = { title: f.title.trim(), summary: f.summary.trim(), ...Object.fromEntries(SECTIONS.map((s) => [s.key, lines(f[s.key])])) };
      const { data } = await api.put(`/sessions/${session.id}`, body);
      qc.setQueryData(["session", session.id], data);
      qc.invalidateQueries({ queryKey: ["sessions"] });
      toast.success("Session updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <EditorSheet open={open} onOpenChange={(v) => { if (v) setF(init()); onOpenChange(v); }} title="Edit session" desc="Free. One point per line." testid="session-edit-sheet">
      <div className="space-y-5">
        <input data-testid="session-edit-title" className="field" maxLength={60} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <input data-testid="session-edit-summary" className="field" maxLength={200} placeholder="Short summary" value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} />
        {SECTIONS.map((s) => (
          <div key={s.key}>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] mb-2" style={{ color: s.color }}>{s.label}</div>
            <textarea data-testid={`session-edit-${s.key}`} rows={3} value={f[s.key]} onChange={(e) => setF({ ...f, [s.key]: e.target.value })} className="w-full bg-[#111113] border border-[#27272A] focus:border-[#E50914] rounded-xl p-3 text-[15px] leading-relaxed outline-none resize-y transition-colors" />
          </div>
        ))}
        <button data-testid="session-edit-save" onClick={save} disabled={busy || !f.title.trim()} className="btn-primary">
          {busy ? <Loader2 size={18} className="animate-spin" /> : "Save changes"}
        </button>
      </div>
    </EditorSheet>
  );
}

export function AddNotesSheet({ session, open, onOpenChange }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: plan } = usePlan();
  const left = aiLeftOf(plan);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${session.id}/add-notes`, { text: text.trim(), timezone: userTimezone() });
      qc.setQueryData(["session", session.id], data);
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["billing"] });
      qc.invalidateQueries({ queryKey: ["tags"] });
      setText("");
      toast.success("Notes added to your session");
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <EditorSheet open={open} onOpenChange={onOpenChange} title="Add notes" desc="Type anything you forgot. The AI adds it to what's already there." testid="session-add-notes-sheet">
      <textarea data-testid="session-add-notes-input" autoFocus rows={5} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Also hit an armbar from mount in the last round. Coach said keep my knees tight." className="w-full bg-[#111113] border border-[#27272A] focus:border-[#E50914] rounded-2xl p-4 text-[16px] leading-relaxed outline-none resize-none transition-colors placeholder:text-[#52525B]" />
      {left !== null && (
        <p className="text-xs text-[#A1A1AA] mt-2 mb-4" data-testid="session-add-notes-cost">
          Uses 1 of your {plan.usage.ai_logs_limit} free AI analyses this week · <span className={left ? "text-white" : "text-[#FF3B30]"}>{left} left</span>
        </p>
      )}
      <button data-testid="session-add-notes-submit" onClick={submit} disabled={busy || text.trim().length < 3 || left === 0} className={`btn-primary ${left === null ? "mt-4" : ""}`}>
        {busy ? <Loader2 size={18} className="animate-spin" /> : <>{left === 0 ? "No free AI analyses left this week" : "Add to session"}</>}
      </button>
    </EditorSheet>
  );
}
